import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Select } from '@aline/common';
import { useTheme } from '@mui/material';
import { usePrevious } from 'react-use';
import { FormSubmitButtons, PrivateCommentToggle } from 'src/components';
import { AlineMarkdownEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/AlineMarkdownEditor';
import type { AlineMarkdownEditorRef } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/types';
import { getCommentMarkdownMentions } from 'src/components/comments/comment-markdown-mentions';
import { PrivateCommentMentionWarning } from 'src/components/comments/PrivateCommentMentionWarning';
import { GrantMentionedUsersAccessDialog } from 'src/components/simple-editor/GrantMentionedUsersAccessDialog';
import { useMentionedAccountsWithoutAccessForUserIds } from 'src/components/simple-editor/hooks';
import type { MentionEntity } from 'src/components/simple-editor/types';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId, useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { documentTypename, useMaybeWorkflowParams } from 'src/helpers';
import { useAuthorizedTransforms, useCreateComment, useGetCurrentDoc } from 'src/hooks';
import { getIsUserInDocOwnerOrg } from 'src/hooks/document';
import { useMaybeEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles()(theme => ({
  buttonContainer: {
    marginTop: theme.spacing(1),
  },
}));

export const addCommentEditorId = 'add-comment-editor';

export function AddCommentForm(): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const editor = useMaybeEditor();
  const {
    comments: { setActivePanel },
  } = useEditorTabStateActions();
  const { viewing } = useEditingMode();
  const { document } = useGetCurrentDoc();
  const currentUser = useSelectCurrentUser();
  const currentUserId = useCurrentUserId();
  const [markdown, setMarkdown] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [mentionedEmails, setMentionedEmails] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const editorRef = useRef<AlineMarkdownEditorRef>(null);
  const canCreatePrivateComment = getIsUserInDocOwnerOrg(currentUser, document);
  const [baseIsPrivate, setIsPrivate] = useState(false);
  const isPrivate = canCreatePrivateComment && baseIsPrivate;
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { documentId, workflowId } = useMaybeWorkflowParams();
  const { createComment, loading: creatingComment } = useCreateComment();
  const [pendingMarkdown, setPendingMarkdown] = useState<string | null>(null);
  const [emailsWithoutAccess, setEmailsWithoutAccess] = useState<string[]>([]);

  const activePanel = useEditorTabStateSelector(state => state.comments.activePanel);
  const prevActivePanel = usePrevious(activePanel);
  const resetEditor = useCallback(() => {
    editorRef.current?.clear();
    setMarkdown('');
    setMentionedUserIds([]);
    setMentionedEmails([]);
    setPendingMarkdown(null);
    setEmailsWithoutAccess([]);
    setIsPrivate(false);
  }, []);

  // Reset and focus editor when panel becomes active
  useEffect(() => {
    if (activePanel === 'AddComment' && prevActivePanel !== 'AddComment') {
      resetEditor();
    }
  }, [activePanel, prevActivePanel, resetEditor]);

  const mentionEntity: MentionEntity = workflowId
    ? { __typename: 'Workflow', id: workflowId }
    : { __typename: 'Document', id: documentId };

  const {
    canEditMentionEntity,
    getEmailsWithoutAccessForUserIds,
    getMentionedExternalUsersForUserIds,
    mentionEntityOwner,
  } = useMentionedAccountsWithoutAccessForUserIds({ isPrivate, mentionEntity });
  const mentionedExternalUsers = getMentionedExternalUsersForUserIds(
    mentionedUserIds,
    mentionedEmails
  );

  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);

  function syncMentionState(nextMarkdown: string): void {
    setMarkdown(nextMarkdown);

    const { directMentionEmails, mentionedUserIds: userMentionIds } = getCommentMarkdownMentions(
      editorRef.current?.getEditor(),
      nextMarkdown
    );
    setMentionedUserIds(userMentionIds);
    setMentionedEmails(directMentionEmails);
  }

  async function handleSubmit(): Promise<void> {
    const trimmed = (editorRef.current?.getMarkdown() ?? markdown).trim();
    if (!trimmed) return;

    const { directMentionEmails, mentionedUserIds: userMentionIds } = getCommentMarkdownMentions(
      editorRef.current?.getEditor(),
      trimmed
    );
    const nextEmailsWithoutAccess = getEmailsWithoutAccessForUserIds(
      userMentionIds,
      directMentionEmails
    );
    if (nextEmailsWithoutAccess.length) {
      setEmailsWithoutAccess(nextEmailsWithoutAccess);
      setPendingMarkdown(trimmed);
      setOpen(true);
      return;
    }

    await handleCreateComment(trimmed);
  }

  async function handleCreateComment(text: string): Promise<void> {
    if (!currentUserId || !editingMode || !document) return;

    const nodeRefId = uuid4();
    const { data } = await createComment(document.id, documentTypename, {
      variables: {
        input: {
          commentProps: {
            authorId: currentUserId,
            commentableId: document.id,
            commentableType: Apollo.CommentableType.Document,
            isPrivate,
            nodeRefId,
            parentCommentId: null,
            text,
          },
        },
      },
    });
    if (!data) return;
    if (editor) {
      AuthorizedTransforms.insertComment(editor, { editingMode, nodeRefId });
      ClientTransforms.focus(editor);
    }
    handleCancel();
  }

  function handleCancel(): void {
    resetEditor();
    setActivePanel('ViewComments');
  }

  return (
    <>
      {isPrivate && <PrivateCommentMentionWarning users={mentionedExternalUsers} />}
      {canCreatePrivateComment && (
        <PrivateCommentToggle isPrivate={baseIsPrivate} onChange={setIsPrivate} />
      )}
      <AlineMarkdownEditor
        key={addCommentEditorId}
        ref={editorRef}
        autoFocus
        dataCy={Select.document.editor.rightDrawer.comments.commentInput}
        maxHeight={160}
        mentions={{
          allowEmailMentions: true,
          documentId,
          enabledTypes: [Apollo.MentionEntitySearchType.User],
          workflowId,
        }}
        minHeight={80}
        placeholder="Add your comment text..."
        readOnly={viewing}
        style={{
          backgroundColor: theme.palette.common.white,
          borderRadius: canCreatePrivateComment ? '0 0 3px 3px' : 3,
        }}
        onChange={syncMentionState}
        onSubmit={handleSubmit}
      />
      <FormSubmitButtons
        className={classes.buttonContainer}
        disableCancel={creatingComment}
        disableSubmit={viewing}
        loading={creatingComment}
        primaryButtonText={'Comment'}
        onClickCancel={handleCancel}
        onClickSubmit={handleSubmit}
      />
      <GrantMentionedUsersAccessDialog
        emailsWithoutAccess={emailsWithoutAccess}
        mentionEntity={mentionEntity}
        mentionEntityOwner={mentionEntityOwner}
        open={open}
        setOpen={setOpen}
        variant={canEditMentionEntity ? 'grant-access' : 'request-access'}
        onSubmit={() => {
          if (!pendingMarkdown) return;

          void handleCreateComment(pendingMarkdown);
        }}
      />
    </>
  );
}
